import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { useAppStore } from "@/store";
import { testerKeys } from "./use-testers";

type FieldCredential = components["schemas"]["FieldCredential"];
type FieldCredentialListResponse = components["schemas"]["FieldCredentialListResponse"];
type FieldCredentialCreate = components["schemas"]["FieldCredentialCreate"];
type FieldCredentialUpdate = components["schemas"]["FieldCredentialUpdate"];
type CredentialActivityScope = components["schemas"]["CredentialActivityScope"];

const LIST_STALE_TIME = 60_000;
const LIST_GC_TIME = 5 * 60_000;

/** Mirrors the listOrgFieldCredentials OpenAPI sort_by enum (the Go ORDER BY allow-list). */
export type FieldCredentialSortBy = "expires_at" | "activity_scope" | "created_at";

export interface FieldCredentialListParams {
  profile_id?: string;
  activity_scope?: CredentialActivityScope;
  /** Date upper bound powering the expiring-within filter. */
  expires_before?: string;
  limit?: number;
  offset?: number;
  sort_by?: FieldCredentialSortBy;
  sort_dir?: "asc" | "desc";
}

/**
 * Query-key factory for field-credential caches. Org-param routes thread
 * orgId through every key. Per-person sub-lists (tester-keyed and
 * member-keyed) live under the same root so `fieldCredentialKeys.all`
 * invalidation flushes them alongside the org-wide list.
 */
export const fieldCredentialKeys = {
  all: ["field-credentials"] as const,
  lists: () => [...fieldCredentialKeys.all, "list"] as const,
  list: (orgId: string, params: FieldCredentialListParams) =>
    [...fieldCredentialKeys.lists(), orgId, params] as const,
  detail: (orgId: string, credentialId: string) =>
    [...fieldCredentialKeys.all, "detail", orgId, credentialId] as const,
  tester: (orgId: string, testerId: string) =>
    [...fieldCredentialKeys.all, "tester", orgId, testerId] as const,
  member: (orgId: string, memberId: string) =>
    [...fieldCredentialKeys.all, "member", orgId, memberId] as const,
} as const;

function withDefaults(params: FieldCredentialListParams): FieldCredentialListParams {
  return {
    ...params,
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
  };
}

/**
 * Org-wide credential list (the expiry dashboard). Person-keyed credential
 * evidence per AWWA M14 §10 per-purveyor verification; holders span testers
 * AND org-member inspectors.
 */
export function useOrgFieldCredentials(params: FieldCredentialListParams = {}) {
  const orgId = useAppStore((s) => s.activeOrgId);
  const merged = withDefaults(params);
  return useQuery<FieldCredentialListResponse>({
    queryKey: fieldCredentialKeys.list(orgId ?? "", merged),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.GET("/orgs/{orgId}/field-credentials", {
        params: { path: { orgId }, query: merged },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to list field credentials";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

/** One credential by row id — the detail / edit page read. */
export function useFieldCredential(credentialId: string | null | undefined) {
  const orgId = useAppStore((s) => s.activeOrgId);
  return useQuery<FieldCredential>({
    queryKey: fieldCredentialKeys.detail(orgId ?? "", credentialId ?? ""),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      if (!credentialId) throw new Error("Credential id is required");
      const { data, error, response } = await apiClient.GET(
        "/orgs/{orgId}/field-credentials/{credentialId}",
        {
          params: { path: { orgId, credentialId } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load credential";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId) && Boolean(credentialId),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
  });
}

/** Credentials held by a tester (org_testers row id; resolved via its profile_id). */
export function useTesterCredentials(testerId: string | null | undefined) {
  const orgId = useAppStore((s) => s.activeOrgId);
  return useQuery<FieldCredentialListResponse>({
    queryKey: fieldCredentialKeys.tester(orgId ?? "", testerId ?? ""),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      if (!testerId) throw new Error("Tester id is required");
      const { data, error, response } = await apiClient.GET(
        "/orgs/{orgId}/testers/{testerId}/credentials",
        {
          params: { path: { orgId, testerId } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to list tester credentials";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId) && Boolean(testerId),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
  });
}

/** Credentials held by an org member (inspector twin; memberId = profile id). */
export function useMemberCredentials(memberId: string | null | undefined) {
  const orgId = useAppStore((s) => s.activeOrgId);
  return useQuery<FieldCredentialListResponse>({
    queryKey: fieldCredentialKeys.member(orgId ?? "", memberId ?? ""),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      if (!memberId) throw new Error("Member id is required");
      const { data, error, response } = await apiClient.GET(
        "/orgs/{orgId}/members/{memberId}/credentials",
        {
          params: { path: { orgId, memberId } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to list member credentials";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId) && Boolean(memberId),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
  });
}

/**
 * Record a credential for an org member (inspector twin). Tester lists are
 * invalidated too: a member can also hold a tester authorization (the
 * in-house tester shape) whose expiry chip derives from credential rows.
 */
export function useCreateMemberCredential(memberId: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (body: FieldCredentialCreate) => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST(
        "/orgs/{orgId}/members/{memberId}/credentials",
        {
          params: { path: { orgId, memberId } },
          body,
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to create credential";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({
          queryKey: fieldCredentialKeys.member(orgId, memberId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: fieldCredentialKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: testerKeys.lists() });
    },
  });
}

/**
 * PATCH a credential by its row id. The holder is not derivable from the
 * credential id alone, so the whole field-credential cache root is flushed
 * (org-wide list + every per-person sub-list) along with the tester lists
 * whose expiry chips derive from credential rows.
 */
export function useUpdateFieldCredential(credentialId: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (body: FieldCredentialUpdate) => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.PATCH(
        "/orgs/{orgId}/field-credentials/{credentialId}",
        {
          params: { path: { orgId, credentialId } },
          body,
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to update credential";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fieldCredentialKeys.all });
      void queryClient.invalidateQueries({ queryKey: testerKeys.lists() });
    },
  });
}

/** Soft-delete a credential. Same broad invalidation rationale as update. */
export function useDeleteFieldCredential(credentialId: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.DELETE(
        "/orgs/{orgId}/field-credentials/{credentialId}",
        {
          params: { path: { orgId, credentialId } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to delete credential";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fieldCredentialKeys.all });
      void queryClient.invalidateQueries({ queryKey: testerKeys.lists() });
    },
  });
}

export { LIST_STALE_TIME, LIST_GC_TIME };

export type {
  FieldCredential,
  FieldCredentialListResponse,
  FieldCredentialCreate,
  FieldCredentialUpdate,
  CredentialActivityScope,
};
